import React, { useEffect, useState, useContext } from 'react';
import { Box, Typography, Chip, Button, useMediaQuery } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ThemeContext } from '../../../lib/contexts/ThemeContext';
import { BenefitDetailsContext } from '../../../lib/contexts/BenefitDetailsContext';
import { differenceInWeeks, isBefore, parseISO, isValid } from 'date-fns';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import useBenefitDetails from '../../../lib/hooks/useBenefitDetails';
import { getClientHost } from '../../../lib/constants/constants';
import useSession from '../../../lib/hooks/useSession';

// Utility function to convert hex to RGB
const hexToRgb = (hex) => {
  hex = hex.replace(/^#/, '');
  let bigint = parseInt(hex, 16);
  let r = (bigint >> 16) & 255;
  let g = (bigint >> 8) & 255;
  let b = bigint & 255;
  return `${r}, ${g}, ${b}`;
};

// Utility function to calculate luminance
const calculateLuminance = (r, g, b) => {
  const a = [r, g, b].map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
};

// Utility function to determine the best contrasting text color
const getContrastingTextColor = (hex) => {
  const rgb = hexToRgb(hex).split(', ').map(Number);
  const luminance = calculateLuminance(rgb[0], rgb[1], rgb[2]);
  return luminance > 0.5 ? '#000' : '#fff';
};

const ProgramInfoCard = ({ programInfo, providerView }) => {
  const isMobileView = useMediaQuery('(max-width:600px)');
  const {
    state: { darkMode },
  } = useContext(ThemeContext);
  const { benefitDetails, mainBenefitDetails } = useContext(BenefitDetailsContext);
  const { data: benefitDetailsData } = useBenefitDetails();
  const [defaultProgramInfo, setDefaultProgramInfo] = useState({
    benefit_name: 'Loading...',
    weeksLeft: 0,
    isActive: false,
    mealsPerWeek: 0,
    totalWeeks: 0,
    auth_start_date: '',
    auth_end_date: '',
    status: 'Loading...',
  });
  const [effectiveProgramInfo, setEffectiveProgramInfo] = useState(defaultProgramInfo);
  const [mappedStatus, setMappedStatus] = useState('Loading...');
  const [workflowData, setWorkflowData] = useState({});
  const { data: session } = useSession();
  useEffect(() => {
    if (session) {
      setWorkflowData({
        currentDietQuality: session?.lastQuality,
        goalDietQuality: session?.goalQuality,
      });
    }
  }, [session]);
  // console.log('session in programinfocard: ', session);
  // console.log('workflowData in programinfocard: ', workflowData);

  useEffect(() => {
    setTimeout(() => {
      setDefaultProgramInfo((prevInfo) => ({
        ...prevInfo,
        benefit_name: 'Medically-Tailored Food Program',
        status: 'No application data',
      }));
    }, 7000);
  }, []);

  useEffect(() => {
    if (programInfo) {
      // console.log('using programInfo in ProgramInfoCard');
      // console.log('here is programinfo: ', programInfo);
      setEffectiveProgramInfo(programInfo);
    } else if (mainBenefitDetails) {
      // console.log('using mainBenefitDetails in ProgramInfoCard: ', mainBenefitDetails);
      setEffectiveProgramInfo(mainBenefitDetails);
    } else {
      setEffectiveProgramInfo(defaultProgramInfo);
    }
  }, [benefitDetails, mainBenefitDetails, defaultProgramInfo]);

  useEffect(() => {
    let status = statusMapList[effectiveProgramInfo.status] || 'Loading...';
    if (status === 'Lead Created') {
      if (workflowData.goalDietQuality) {
        status = 'In Progress';
      } else if (!workflowData.currentDietQuality) {
        status = 'Not Started';
      }
    }
    if (status === 'Ready for Re-Auth') {
      // console.log('here is effectiveProgramInfo.auth_end_date: ', effectiveProgramInfo.auth_end_date);
      // console.log(
      //   'here is new Date(effectiveProgramInfo.auth_end_date): ',
      //   new Date(effectiveProgramInfo.auth_end_date),
      // );
      // console.log('here is new Date(): ', new Date());
      // console.log(
      //   'here is new Date(effectiveProgramInfo.auth_end_date) > new Date(): ',
      //   new Date(effectiveProgramInfo.auth_end_date) > new Date(),
      // );
      if (new Date(effectiveProgramInfo?.auth_end_date) > new Date()) {
        status = 'Active - Time to Renew';
      } else {
        status = 'Re-Apply';
      }
    }
    // console.log('status in programinfocard : ', status);
    setMappedStatus(status);
  }, [mainBenefitDetails, effectiveProgramInfo]);

  const applyURL = getClientHost() + '/assessment/start';

  // const statusMapList = {
  //   Canceled: 'Canceled',
  //   Cancelled: 'Canceled',
  //   'Active:NO_AUTH_END': 'Active',
  //   Approved: 'Approved',
  //   Active: 'Active',
  //   Accepted: 'Accepted',
  //   Completed: 'Completed',
  //   Submitted: 'Pending',
  //   Eligible: 'Eligible',
  //   Ineligible: 'Not Eligible',
  //   'In Process': 'Pending',
  //   'In Review': 'Pending',
  //   'Incomplete application': 'Incomplete',
  //   'No application data': 'Not Started',
  //   'Active - Re-Auth Available': 'Active - Time to Renew',
  //   Waitlist: 'Waitlisted',
  // };
  const statusMapList = {
    'Lead Created': 'Lead Created', // 'Not Started' or 'In Progress', depending on the progress of the users assessment
    'Eligible Lead': 'In Review',
    'Ready for Re-Auth': 'Ready for Re-Auth', // triggers a push notification to user depending on expiration date
    'Re-Auth Eligible': 'In Review',
    'Re-Auth Verified': 'In Review',
    'RD Needed': 'In Review',
    'MNT Needed': 'Action Required',
    'Ready to Submit': 'In Review',
    'Submitted to Payer': 'Awaiting Approval',
    Activated: 'Activated',
    Canceled: 'Canceled',
    Completed: 'Completed',
    Ineligible: 'Not Eligible',
    Canceled: 'Canceled',
    Waitlisted: 'Waitlisted',
  };

  const calculateWeeksLeft = () => {
    const currentDate = new Date();
    const start = parseISO(effectiveProgramInfo?.auth_start_date);
    const end = parseISO(effectiveProgramInfo?.auth_end_date);

    if (!isValid(start) || !isValid(end)) {
      return 'Invalid date';
    }

    if (isBefore(currentDate, start)) {
      return 'Program not started';
    }

    return differenceInWeeks(end, currentDate) + ' Weeks Left';
  };

  const getBackgroundColorForBoxRight = () => {
    if (benefitDetails?.status === 'Active' || benefitDetails?.status === 'Not Eligible') {
      return 'grey';
    }
    if (benefitDetails?.status === 'Action Required') {
      return '#fff4a4';
    }
    return 'white';
  };

  const getStatusChipColor = (status) => {
    switch (status) {
      case 'Active':
        return darkMode ? '#1B5E20' : '#2E7D32';
      case 'Pending' || 'Eligible':
        return darkMode ? '#FF8F00' : '#FFA000';
      case 'NotEligible':
        return darkMode ? '#757575' : '#BDBDBD';
      case 'Action Required':
        return darkMode ? '#b38f00' : '#fff4a4';
      default:
        return darkMode ? '#616161' : '#9E9E9E';
    }
  };

  const getButtonLabel = (actionRequired) => {
    switch (actionRequired) {
      case 'Meet with Your Tangelo Dietitian':
        return 'Schedule Now';
      case 'Finish Applicaiton':
        return 'Finish Now';
      case 'Application Not Approved':
      default:
        return 'More Information';
    }
  };

  // Calculate weeks left
  let weeksLeft;
  if (effectiveProgramInfo?.auth_end_date) {
    weeksLeft = differenceInWeeks(new Date(effectiveProgramInfo?.auth_end_date), new Date());
  } else {
    weeksLeft = 'Loading...';
  }

  let chipColor = '#007E66';
  let textColor = 'var(--color1, #FDFDFD)';
  let chipStyles = {
    display: 'flex',
    // width: '99px',
    height: '35px',
    padding: '8px 12px',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8.391px',
    borderRadius: '16px',
    background: chipColor,
  };

  if (
    mappedStatus === 'Pending' ||
    mappedStatus === 'Not Started' ||
    mappedStatus === 'Incomplete' ||
    mappedStatus === 'Active - Time to Renew' ||
    mappedStatus === 'Waitlisted' ||
    mappedStatus === 'In Review' ||
    mappedStatus === 'Awaiting Approval' ||
    mappedStatus === 'Completed' ||
    mappedStatus === 'Canceled'
  ) {
    chipColor = '#E2E2E2';
    textColor = '#000';
  }
  if (mappedStatus === 'Action Required' || mappedStatus === 'Not Eligible') {
    chipColor =
      mappedStatus === 'Action Required' ? '#FEF2A4' : mappedStatus === 'Not Eligible' ? '#E8E8E8' : chipColor;
    textColor = '#000';
    chipStyles = {
      ...chipStyles,
      height: '36.713px',
      width: 'auto',
    };
  }

  chipStyles.background = chipColor;

  const getRightBox = () => {
    if (
      mappedStatus === 'Eligible' ||
      mappedStatus === 'Active' ||
      mappedStatus === 'Completed' ||
      mappedStatus === 'Approved' ||
      mappedStatus === 'Accepted'
    ) {
      return (
        <Box
          id="program-benefit-info-eligible"
          sx={{
            display: 'flex',
            maxWidth: '380px',
            // minWidth: '400px',
            padding: '18px 24px',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            alignSelf: 'stretch',
            borderRadius: isMobileView ? '0px 0px 8px 8px' : '0px 8px 8px 0px',
            borderRight: isMobileView ? '2px solid var(--color5, #E1DDED)' : 'none',
            borderBottom: isMobileView ? '2px solid var(--color5, #E1DDED)' : 'none',
            borderLeft: isMobileView ? '2px solid var(--color5, #E1DDED)' : 'none',
            background: 'var(--color4, #EBE8F3)',
          }}
        >
          <Box
            id="benefit-info"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '12px',
              alignSelf: 'stretch',
            }}
          >
            <Box
              id="benefit-header"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '12px',
              }}
            >
              <Typography
                sx={{
                  color: 'var(--Gray-gray12, #171717)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontFamily: 'Montserrat',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: 'normal',
                }}
              >
                Benefit Details
              </Typography>
              <Typography
                sx={{
                  color: 'var(--Gray-gray12, #171717)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontFamily: 'Montserrat',
                  fontSize: '18px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: 'normal',
                }}
              >
                {/* {programInfo.applicationStatus?.progressNote || 'No progress note available'} */}
                14 Weeks of Medically-Tailored Food
              </Typography>
            </Box>
            {/* <Typography
            sx={{
              color: 'var(--Gray-gray12, #171717)',
              alignSelf: 'stretch',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '17px', // 121.429%
            }}
          >
            {programInfo.applicationStatus?.message || 'No message available'}
          </Typography> */}
          </Box>
          <Box
            id="benefit-dates"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '12px',
              alignSelf: 'stretch',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'row', marginTop: '20px' }}>
              <Typography
                sx={{
                  color: 'var(--Gray-gray12, #171717)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: 'normal',
                }}
              >
                Start Date:
              </Typography>
              <Typography
                sx={{
                  marginLeft: '4px',
                  color: 'var(--Gray-gray12, #171717)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: '17px',
                }}
              >
                {effectiveProgramInfo?.auth_start_date}
                {/* Jan 20, 2024 */}
              </Typography>
            </div>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <Typography
                sx={{
                  color: 'var(--Gray-gray12, #171717)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: 'normal',
                }}
              >
                End Date:
              </Typography>
              <Typography
                sx={{
                  marginLeft: '4px',
                  color: 'var(--Gray-gray12, #171717)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: '17px',
                }}
              >
                {effectiveProgramInfo?.auth_end_date}
                {/* March 12, 2024 */}
              </Typography>
            </div>
          </Box>
        </Box>
      );
    }
    if (mappedStatus === 'Not Eligible') {
      return (
        <Box
          id="program-benefit-info-not-eligible"
          sx={{
            display: 'flex',
            maxWidth: '380px',
            padding: '24px 24px 18px 24px',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            flex: '1 0 0',
            alignSelf: 'stretch',
            background: 'var(--Gray-gray5, #E8E8E8)',
          }}
        >
          <Box
            id="not-eligible-message"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '20px',
              alignSelf: 'stretch',
            }}
          >
            <Box
              id="not-eligible-message-title"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '12px',
              }}
            >
              <Typography
                sx={{
                  color: 'var(--Gray-gray12, #171717)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontFamily: 'Montserrat',
                  fontSize: '18px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: 'normal',
                }}
              >
                Application Not Approved
              </Typography>
            </Box>
            <Typography
              sx={{
                alignSelf: 'stretch',
                color: 'var(--Gray-gray12, #171717)',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '17px', // 121.429%
              }}
            >
              Looks like you didn't qualify for our{' '}
              {effectiveProgramInfo?.benefit_name
                ? effectiveProgramInfo?.benefit_name
                : 'Medically-Tailored Food Program'}
            </Typography>
          </Box>
          <Button
            id="more-information-button"
            sx={{
              display: 'flex',
              height: 'var(--3, 36px)',
              padding: 'var(--0, 0px) var(--3, 13px)',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 'var(--3, 13px)',
              alignSelf: 'stretch',
              borderRadius: 'var(--3, 7px)',
              border: 'var(--1, 2px) solid var(--color12, #1C124D)',
              background: 'var(--color1, #FDFDFD)',
            }}
            onClick={() => handleMoreInfoClick('denied')}
          >
            <InfoIcon sx={{ width: 'var(--1, 20px)', height: 'var(--1, 20px)' }} />
            <Typography
              sx={{
                color: 'var(--color12, #1C124D)',
                textAlign: 'center',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: 'normal',
              }}
            >
              More Information
            </Typography>
          </Button>
        </Box>
      );
    }
    if (mappedStatus === 'Action Required') {
      return (
        <Box
          id="program-benefit-info-action-required"
          sx={{
            display: 'flex',
            maxWidth: '380px',
            padding: '18px 16px',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '16px',
            alignSelf: 'stretch',
            background: 'var(--Yellow-yellow5, #FEF2A4)',
          }}
        >
          <Box
            id="action-required-message-container"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '20px',
              alignSelf: 'stretch',
            }}
          >
            <Box
              id="action-required-message"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '12px',
                alignSelf: 'stretch',
              }}
            >
              <Box
                id="action-required-message-title"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '12px',
                }}
              >
                <Typography
                  sx={{
                    color: 'var(--Gray-gray12, #171717)',
                    leadingTrim: 'both',
                    textEdge: 'cap',
                    fontFamily: 'Montserrat',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: 'normal',
                  }}
                >
                  Action Required
                </Typography>
                <Typography
                  sx={{
                    color: 'var(--Gray-gray12, #171717)',
                    leadingTrim: 'both',
                    textEdge: 'cap',
                    fontFamily: 'Montserrat',
                    fontSize: '18px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: 'normal',
                  }}
                >
                  Book A Virtual Dietitian Visit
                </Typography>
              </Box>
              <Typography
                sx={{
                  alignSelf: 'stretch',
                  color: 'var(--Gray-gray12, #171717)',
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '17px', // 121.429%
                }}
              >
                Book time with a registered dietitian to review your Diet Quality Results.
              </Typography>
            </Box>
          </Box>
          <Button
            id="schedule-now-button"
            sx={{
              display: 'flex',
              height: 'var(--3, 36px)',
              padding: 'var(--0, 0px) var(--3, 13px)',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 'var(--3, 13px)',
              alignSelf: 'stretch',
              borderRadius: 'var(--3, 7px)',
              border: 'var(--1, 2px) solid var(--color12, #1C124D)',
              background: 'var(--color1, #FDFDFD)',
            }}
            onClick={() => window.open(applyURL, '_blank')}
          >
            <Typography
              sx={{
                color: 'var(--color12, #1C124D)',
                textAlign: 'center',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: 'normal',
              }}
            >
              Complete Application
            </Typography>
            <ArrowForwardIcon
              sx={{
                width: 'var(--1, 20px)',
                height: 'var(--1, 20px)',
              }}
            />
          </Button>
        </Box>
      );
    }
    if (mappedStatus === 'Incomplete' || mappedStatus === 'Not Started') {
      return (
        <Box
          id="program-benefit-info-action-required"
          sx={{
            display: 'flex',
            maxWidth: '380px',
            padding: '18px 16px',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '16px',
            alignSelf: 'stretch',
            background: 'var(--Yellow-yellow5, #FEF2A4)',
          }}
        >
          <Box
            id="action-required-message-container"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '20px',
              alignSelf: 'stretch',
            }}
          >
            <Box
              id="action-required-message"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '12px',
                alignSelf: 'stretch',
              }}
            >
              <Box
                id="action-required-message-title"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '12px',
                }}
              >
                <Typography
                  sx={{
                    color: 'var(--Gray-gray12, #171717)',
                    leadingTrim: 'both',
                    textEdge: 'cap',
                    fontFamily: 'Montserrat',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: 'normal',
                  }}
                >
                  Action Required
                </Typography>
                <Typography
                  sx={{
                    color: 'var(--Gray-gray12, #171717)',
                    leadingTrim: 'both',
                    textEdge: 'cap',
                    fontFamily: 'Montserrat',
                    fontSize: '18px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: 'normal',
                  }}
                >
                  Finish Application
                </Typography>
              </Box>
              <Typography
                sx={{
                  alignSelf: 'stretch',
                  color: 'var(--Gray-gray12, #171717)',
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '17px', // 121.429%
                }}
              >
                Once complete, our team can check your eligibility for our Medically-Tailored Food Program with your
                health plan.
              </Typography>
            </Box>
          </Box>
          <Button
            id="schedule-now-button"
            onClick={() => window.open(applyURL, '_blank')}
            sx={{
              display: 'flex',
              height: 'var(--3, 36px)',
              padding: 'var(--0, 0px) var(--3, 13px)',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 'var(--3, 13px)',
              alignSelf: 'stretch',
              borderRadius: 'var(--3, 7px)',
              border: 'var(--1, 2px) solid var(--color12, #1C124D)',
              background: 'var(--color1, #FDFDFD)',
            }}
          >
            <Typography
              sx={{
                color: 'var(--color12, #1C124D)',
                textAlign: 'center',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: 'normal',
              }}
            >
              Complete Application
            </Typography>
            <ArrowForwardIcon
              sx={{
                width: 'var(--1, 20px)',
                height: 'var(--1, 20px)',
              }}
            />
          </Button>
        </Box>
      );
    }
    if (mappedStatus === 'Loading...') {
      return (
        <Box
          id="program-benefit-info-not-eligible"
          sx={{
            display: 'flex',
            maxWidth: '380px',
            padding: '24px 24px 18px 24px',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            flex: '1 0 0',
            alignSelf: 'stretch',
            gap: 'var(--3, 13px)',
            background: 'var(--Gray-gray5, #E8E8E8)',
          }}
        >
          <Box
            id="not-eligible-message"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '12px',
              alignSelf: 'stretch',
            }}
          >
            <Box
              id="not-eligible-message-title"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '12px',
              }}
            >
              <Typography
                sx={{
                  color: 'var(--Gray-gray12, #171717)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontFamily: 'Montserrat',
                  fontSize: '18px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: 'normal',
                }}
              >
                {effectiveProgramInfo?.benefit_name
                  ? effectiveProgramInfo?.benefit_name
                  : 'Medically-Tailored Food Program'}
              </Typography>
            </Box>
            <Typography
              sx={{
                alignSelf: 'stretch',
                color: 'var(--Gray-gray12, #171717)',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '17px', // 121.429%
              }}
            ></Typography>
          </Box>
          <Button
            id="more-information-button"
            sx={{
              display: 'flex',
              height: 'var(--3, 36px)',
              padding: 'var(--0, 0px) var(--3, 13px)',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 'var(--3, 13px)',
              alignSelf: 'stretch',
              borderRadius: 'var(--3, 7px)',
              border: 'var(--1, 2px) solid var(--color12, #1C124D)',
              background: 'var(--color1, #FDFDFD)',
            }}
          >
            <Typography
              sx={{
                color: 'var(--color12, #1C124D)',
                textAlign: 'center',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: 'normal',
              }}
            >
              Loading...
            </Typography>
            <ArrowForwardIcon
              style={{
                width: 'var(--1-5, 24px)',
                height: '24px',
              }}
            />
          </Button>
        </Box>
      );
    }
    if (
      effectiveProgramInfo?.status === 'In Process' ||
      effectiveProgramInfo?.status === 'Pending Evaluation' ||
      effectiveProgramInfo?.status === 'In Review' ||
      effectiveProgramInfo?.status === 'Pending' ||
      effectiveProgramInfo == null
    ) {
      return (
        <Box
          id="program-benefit-info-pending"
          sx={{
            display: 'flex',
            maxWidth: '380px',
            padding: '18px 16px',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '16px',
            alignSelf: 'stretch',
            borderRadius: '0px 8px 8px 0px',
            borderTop: '2px solid var(--color4, #EBE8F3)',
            borderRight: '2px solid var(--color4, #EBE8F3)',
            borderBottom: '2px solid var(--color4, #EBE8F3)',
          }}
        >
          <Box
            id="pending-message-container"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '20px',
              alignSelf: 'stretch',
            }}
          >
            <Box
              id="pending-message-box"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '12px',
                alignSelf: 'stretch',
              }}
            >
              <Typography
                sx={{
                  alignSelf: 'stretch',
                  color: 'var(--Gray-gray12, #171717)',
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '17px', // 121.429%
                }}
              >
                We're working on confirming your eligibility for our Medically-Tailored Food Program with your health
                plan.
              </Typography>
              <Box
                id="average-response-time-message-box"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 'var(--4-5, 21px)',
                  alignSelf: 'stretch',
                  borderRadius: '8px',
                  background: 'var(--color4, #EBE8F3)',
                }}
              >
                <Box
                  id="average-response-time-message"
                  sx={{
                    display: 'flex',
                    height: '40px',
                    padding: '12px 8px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '4px',
                    alignSelf: 'stretch',
                    borderRadius: '8px',
                  }}
                >
                  <Box
                    id="average-response-time-message-text-container"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    <Typography
                      sx={{
                        color: 'var(--Gray-gray12, #171717)',
                        leadingTrim: 'both',
                        textEdge: 'cap',
                        fontFamily: 'Montserrat',
                        fontSize: '16px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: 'normal',
                      }}
                    >
                      Avg. Response time:
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      color: 'var(--Gray-gray12, #171717)',
                      leadingTrim: 'both',
                      textEdge: 'cap',
                      fontFamily: 'Montserrat',
                      fontSize: '18px',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      lineHeight: 'normal',
                    }}
                  >
                    10 days
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          <Button
            id="more-information-button"
            sx={{
              display: 'flex',
              height: 'var(--3, 36px)',
              padding: 'var(--0, 0px) var(--3, 13px)',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 'var(--3, 13px)',
              alignSelf: 'stretch',
              borderRadius: 'var(--3, 7px)',
              border: 'var(--1, 2px) solid var(--color12, #1C124D)',
              background: 'var(--color1, #FDFDFD)',
            }}
            onClick={() => handleMoreInfoClick('pending')}
          >
            <InfoIcon sx={{ width: 'var(--1, 20px)', height: 'var(--1, 20px)' }} />
            <Typography
              sx={{
                color: 'var(--color12, #1C124D)',
                textAlign: 'center',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: 'normal',
              }}
            >
              More Information
            </Typography>
          </Button>
        </Box>
      );
    }
    if (mappedStatus === 'Waitlisted') {
      return (
        <Box
          id="program-benefit-info-not-eligible"
          sx={{
            display: 'flex',
            maxWidth: '380px',
            padding: '24px 24px 18px 24px',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            flex: '1 0 0',
            alignSelf: 'stretch',
            background: 'var(--Gray-gray5, #E8E8E8)',
          }}
        >
          <Box
            id="not-eligible-message"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '12px',
              alignSelf: 'stretch',
            }}
          >
            <Box
              id="not-eligible-message-title"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '12px',
              }}
            >
              <Typography
                sx={{
                  color: 'var(--Gray-gray12, #171717)',
                  leadingTrim: 'both',
                  textEdge: 'cap',
                  fontFamily: 'Montserrat',
                  fontSize: '18px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: 'normal',
                }}
              >
                You're On the Waitlist
              </Typography>
            </Box>
            <Typography
              sx={{
                alignSelf: 'stretch',
                color: 'var(--Gray-gray12, #171717)',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontFamily: 'Montserrat',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
              }}
            >
              Your health plan doesn't currently offer a Medically-Tailored Food Program with us, but we're working to
              change that. We'll reach out if they start to offer a program with us!
            </Typography>
          </Box>
        </Box>
      );
    }
    return (
      <Box
        id="program-benefit-info-not-eligible"
        sx={{
          display: 'flex',
          maxWidth: '380px',
          padding: '24px 24px 18px 24px',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          flex: '1 0 0',
          alignSelf: 'stretch',
          gap: 'var(--3, 13px)',
          background: 'var(--Gray-gray5, #E8E8E8)',
        }}
      >
        <Box
          id="not-eligible-message"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '12px',
            alignSelf: 'stretch',
          }}
        >
          <Box
            id="not-eligible-message-title"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '12px',
            }}
          >
            <Typography
              sx={{
                color: 'var(--Gray-gray12, #171717)',
                leadingTrim: 'both',
                textEdge: 'cap',
                fontFamily: 'Montserrat',
                fontSize: '18px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: 'normal',
              }}
            >
              Start Your Application
            </Typography>
          </Box>
          <Typography
            sx={{
              alignSelf: 'stretch',
              color: 'var(--Gray-gray12, #171717)',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '17px', // 121.429%
            }}
          >
            Apply now to see if you qualify for our Medically-Tailored Food Program and access nutritious meals or
            groceries tailored to your health needs.
          </Typography>
        </Box>
        <Button
          id="more-information-button"
          sx={{
            display: 'flex',
            height: 'var(--3, 36px)',
            padding: 'var(--0, 0px) var(--3, 13px)',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 'var(--3, 13px)',
            alignSelf: 'stretch',
            borderRadius: 'var(--3, 7px)',
            border: 'var(--1, 2px) solid var(--color12, #1C124D)',
            background: 'var(--color1, #FDFDFD)',
          }}
          onClick={() => window.open(applyURL, '_blank')}
        >
          <Typography
            sx={{
              color: 'var(--color12, #1C124D)',
              textAlign: 'center',
              leadingTrim: 'both',
              textEdge: 'cap',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: 'normal',
            }}
          >
            Apply Now
          </Typography>
          <ArrowForwardIcon
            style={{
              width: 'var(--1-5, 24px)',
              height: '24px',
            }}
          />
        </Button>
      </Box>
    );
  };

  const handleMoreInfoClick = (path = 'denied') => {
    if (path === 'denied') {
      window.open(
        'https://help.jointangelo.com/en/articles/10346819-what-happens-if-my-application-is-denied',
        '_blank',
      );
    } else if (path === 'pending') {
      window.open('https://help.jointangelo.com/en/articles/10346696-what-happens-after-i-apply', '_blank');
    }
  };
  // console.log('effectiveProgramInfo.status', effectiveProgramInfo.status);
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobileView ? 'column' : 'row',
        alignItems: 'center',
        flex: '1 0 0',
        alignSelf: 'stretch',
      }}
    >
      {/* {isMobileView && (
        <Button
          sx={{
            display: 'flex',
            height: 'var(--4, 44px)',
            padding: 'var(--0, 0px) var(--2, 7px)',
            alignItems: 'center',
            gap: 'var(--4, 18px)',
            alignSelf: 'stretch',
            borderBottom: '2px solid var(--color3, #F5F4F9)',
            justifyContent: isMobileView ? 'flex-start' : 'center',
          }}
        >
          <Typography
            sx={{
              color: 'var(--color12, #1C124D)',
              textAlign: 'center',
              leadingTrim: 'both',
              textEdge: 'cap',
              fontFamily: 'Montserrat',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: 'normal',
              textAlign: isMobileView ? 'left' : 'center',
            }}
          >
            Program Details
          </Typography>
          <ArrowForwardIcon
            style={{
              width: 'var(--1-5, 24px)',
              height: '24px',
            }}
          />
        </Button>
      )} */}
      <Box
        id="program-info"
        sx={{
          // minWidth: '400px',
          maxWidth: '380px',
          display: 'flex',
          minWidth: '280px',
          padding: '18px 24px',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '14px',
          flex: '1 0 0',
          alignSelf: 'stretch',
          borderRadius: isMobileView ? '8px 8px 0px 0px' : '8px 0px 0px 8px',
          borderTop: '2px solid var(--color4, #EBE8F3)',
          borderBottom: isMobileView ? 'none' : '2px solid var(--color4, #EBE8F3)',
          borderRight: isMobileView || providerView ? '2px solid var(--color4, #EBE8F3)' : 'none',
          borderLeft: '2px solid var(--color4, #EBE8F3)',
          background: mappedStatus === 'Active' && providerView ? 'rgba(46, 125, 50, 0.1)' : 'var(--color2, #FBFBFC)',
        }}
      >
        <Typography
          sx={{
            color: 'var(--Gray-gray12, #171717)',
            leadingTrim: 'both',
            textEdge: 'cap',
            fontFamily: 'Montserrat',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: 'normal',
          }}
        >
          Your Program
        </Typography>
        <Box sx={{ flex: '1 0 0' }}>
          <Typography
            sx={{
              color: 'var(--Gray-gray12, #171717)',
              fontFamily: 'Montserrat',
              fontSize: '24px',
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: '125%', // 30px
            }}
          >
            {/* {mappedStatus === 'Active' && effectiveProgramInfo.benefit_name ? effectiveProgramInfo.benefit_name : 'Medically-Tailored Food Program'} */}
            {effectiveProgramInfo?.benefit_name
              ? effectiveProgramInfo?.benefit_name
              : 'Medically-Tailored Food Program'}
          </Typography>
        </Box>
        <Box
          id="program-timeline"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '24px',
            alignSelf: 'stretch',
          }}
        >
          <Chip
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {mappedStatus === 'Action Required' && (
                  <WarningIcon
                    style={{
                      width: 'var(--1, 20px)',
                      height: 'var(--1, 20px)',
                    }}
                  />
                )}
                <Typography
                  sx={{
                    color: textColor,
                    textAlign: 'center',
                    leadingTrim: 'both',
                    textEdge: 'cap',
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: 'normal',
                  }}
                >
                  {mappedStatus}
                </Typography>
              </Box>
            }
            sx={chipStyles}
          />
          <Typography
            sx={{
              color: 'var(--Gray-gray12, #171717)',
              leadingTrim: 'both',
              textEdge: 'cap',
              fontFamily: 'Montserrat',
              fontSize: '18px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: 'normal',
            }}
          >
            {effectiveProgramInfo?.auth_end_date && weeksLeft} {effectiveProgramInfo?.auth_end_date && 'weeks left'}
          </Typography>
        </Box>
      </Box>
      {!providerView && getRightBox()}
    </Box>
  );
};

export default ProgramInfoCard;
